import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getContentModelId, getContentModelName, getLanguage } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getIsLandingPage, getIsSitePage } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { getIsSuggested } from 'ContentEditorUI/redux/selectors/publishSelectors';
import { getOpenModal } from 'ContentEditorUI/redux/selectors/modalSelectors';
import { getActiveSidebarPanel, getEditSidebarActiveTab, getSelectedField, getSidebarCategory, getSidebarSearchQuery } from 'ContentEditorUI/redux/selectors/sidebarSelectors';
import { getSelectedGridLayoutModuleNode, getSelectedGridLayoutNode, getSelectedModuleInTree, getSelectedModuleName, getSelectedRowOrColumn } from 'ContentEditorUI/redux/selectors/selectionSelectors';
import { getPublishedOrStagingUrl } from 'page-editor-ui/redux/selectors/urlSelectors';
import { CANVAS_SECTION_ID, MAX_SURFACE_MEMORY_VALUE_LENGTH, MODAL_SECTION_ID, PAGE_EDITOR_SECTION_ID, SELECTION_SECTION_ID, SIDEBAR_SECTION_ID } from 'page-editor-ui/copilot/surfaceMemory/constants';
const TRUNCATION_SUFFIX = '…';
export function truncateSurfaceMemoryValue(value, maxLength = MAX_SURFACE_MEMORY_VALUE_LENGTH) {
  if (!value || maxLength <= 0) {
    return '';
  }
  if (value.length <= maxLength) {
    return value;
  }
  const hardCut = value.slice(0, maxLength);
  const lastSpace = hardCut.lastIndexOf(' ');
  const softCut = lastSpace > maxLength / 2 ? hardCut.slice(0, lastSpace) : hardCut;
  return `${softCut}${TRUNCATION_SUFFIX}`;
}
export function buildDescription(fields) {
  const description = {};
  Object.keys(fields).forEach(key => {
    const value = fields[key];
    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (trimmedValue !== '') {
        description[key] = truncateSurfaceMemoryValue(trimmedValue);
      }
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      description[key] = value;
    }
  });
  return description;
}
function readCompactValue(value, fieldNames) {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  const record = value;
  for (const fieldName of fieldNames) {
    const directValue = record[fieldName];
    if (typeof directValue === 'string' || typeof directValue === 'number' || typeof directValue === 'boolean') {
      return directValue;
    }
    if (typeof record.get === 'function') {
      const getterValue = record.get(fieldName);
      if (typeof getterValue === 'string' || typeof getterValue === 'number' || typeof getterValue === 'boolean') {
        return getterValue;
      }
    }
  }
  return undefined;
}
function compactSelectionLabel(value) {
  if (typeof value === 'string') {
    return value;
  }
  const compactValue = readCompactValue(value, ['label', 'name', 'title', 'displayName', 'fieldKey', 'id', 'module_id', 'moduleId']);
  return compactValue === undefined ? undefined : String(compactValue);
}
function compactNodeType(value) {
  const compactValue = readCompactValue(value, ['type', 'nodeType', 'moduleType', 'tagName']);
  return compactValue === undefined ? undefined : String(compactValue);
}
function compactNodeId(value) {
  const compactValue = readCompactValue(value, ['id', 'module_id', 'moduleId', 'fieldKey', 'name']);
  if (compactValue === undefined) {
    return undefined;
  }
  return typeof compactValue === 'boolean' ? String(compactValue) : compactValue;
}
function getPageType({
  isLandingPage,
  isSitePage
}) {
  if (isLandingPage) {
    return 'landing page';
  }
  if (isSitePage) {
    return 'site page';
  }
  return 'page';
}
function getSelectionType({
  selectedModuleName,
  selectedFieldLabel,
  selectedRowOrColumnLabel,
  selectedGridLayoutModuleLabel,
  selectedGridLayoutNodeLabel
}) {
  if (selectedModuleName || selectedGridLayoutModuleLabel) {
    return 'module';
  }
  if (selectedFieldLabel) {
    return 'field';
  }
  if (selectedRowOrColumnLabel) {
    return 'row or column';
  }
  if (selectedGridLayoutNodeLabel) {
    return 'layout node';
  }
  return undefined;
}
export function usePageEditorSurfaceMemory() {
  const contentId = useSelector(getContentModelId);
  const pageName = useSelector(getContentModelName);
  const language = useSelector(getLanguage);
  const isLandingPage = useSelector(getIsLandingPage);
  const isSitePage = useSelector(getIsSitePage);
  const isSuggested = useSelector(getIsSuggested);
  const publishedOrStagingUrl = useSelector(getPublishedOrStagingUrl);
  const activeSidebarPanel = useSelector(getActiveSidebarPanel);
  const selectedSidebarCategory = useSelector(getSidebarCategory);
  const editSidebarActiveTab = useSelector(getEditSidebarActiveTab);
  const sidebarSearchQuery = useSelector(getSidebarSearchQuery);
  const selectedField = useSelector(getSelectedField);
  const selectedModuleInTree = useSelector(getSelectedModuleInTree);
  const selectedModuleName = useSelector(getSelectedModuleName);
  const selectedRowOrColumn = useSelector(getSelectedRowOrColumn);
  const selectedGridLayoutNode = useSelector(getSelectedGridLayoutNode);
  const selectedGridLayoutModuleNode = useSelector(getSelectedGridLayoutModuleNode);
  const openModal = useSelector(getOpenModal);
  return useMemo(() => {
    const selectedFieldLabel = compactSelectionLabel(selectedField);
    const selectedRowOrColumnLabel = compactSelectionLabel(selectedRowOrColumn);
    const selectedGridLayoutNodeLabel = compactSelectionLabel(selectedGridLayoutNode);
    const selectedGridLayoutModuleLabel = compactSelectionLabel(selectedGridLayoutModuleNode);
    const selectionType = getSelectionType({
      selectedModuleName,
      selectedFieldLabel,
      selectedRowOrColumnLabel,
      selectedGridLayoutModuleLabel,
      selectedGridLayoutNodeLabel
    });
    const openModalName = typeof openModal === 'string' || typeof openModal === 'number' ? String(openModal) : undefined;
    const sections = [{
      sectionId: PAGE_EDITOR_SECTION_ID,
      description: buildDescription({
        label: 'Page being edited',
        contentId,
        pageName,
        pageType: getPageType({
          isLandingPage,
          isSitePage
        }),
        language,
        publishedOrStagingUrl,
        isSuggested: Boolean(isSuggested)
      })
    }, {
      sectionId: SIDEBAR_SECTION_ID,
      description: buildDescription({
        label: 'Page editor sidebar',
        activePanel: activeSidebarPanel,
        selectedSidebarCategory,
        activeEditFormTab: editSidebarActiveTab,
        selectedField: selectedFieldLabel,
        sidebarSearchQuery
      })
    }, {
      sectionId: CANVAS_SECTION_ID,
      description: buildDescription({
        label: 'Page preview canvas',
        hasSelectedContent: Boolean(selectionType)
      })
    }];
    if (selectionType) {
      sections.push({
        sectionId: SELECTION_SECTION_ID,
        description: buildDescription({
          label: 'Currently selected page editor content',
          selectionType,
          moduleName: selectedModuleName,
          moduleId: compactNodeId(selectedModuleInTree),
          moduleType: compactNodeType(selectedModuleInTree),
          selectedField: selectedFieldLabel,
          rowOrColumn: selectedRowOrColumnLabel,
          layoutNode: selectedGridLayoutNodeLabel,
          layoutNodeId: compactNodeId(selectedGridLayoutNode),
          layoutNodeType: compactNodeType(selectedGridLayoutNode),
          gridLayoutModule: selectedGridLayoutModuleLabel,
          gridLayoutModuleId: compactNodeId(selectedGridLayoutModuleNode),
          gridLayoutModuleType: compactNodeType(selectedGridLayoutModuleNode)
        })
      });
    }
    if (openModalName) {
      sections.push({
        sectionId: MODAL_SECTION_ID,
        description: buildDescription({
          label: 'Open modal',
          modalName: openModalName
        })
      });
    }
    return sections;
  }, [activeSidebarPanel, contentId, editSidebarActiveTab, isLandingPage, isSitePage, isSuggested, language, openModal, pageName, publishedOrStagingUrl, selectedField, selectedGridLayoutModuleNode, selectedGridLayoutNode, selectedModuleInTree, selectedModuleName, selectedRowOrColumn, selectedSidebarCategory, sidebarSearchQuery]);
}